import { Button } from "@groot/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@groot/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@groot/ui/dialog";
import { Input } from "@groot/ui/input";
import { Label } from "@groot/ui/label";
import { Switch } from "@/app/shared/ui/switch";
import {
  useCategories,
  useCreateCategory,
  useDeleteCategory,
  useUpdateCategory,
} from "@/hooks/api/useCategories";
import type { Category, CreateCategoryDTO, UpdateCategoryDTO } from "@/types";
import { ChevronDown, ChevronRight, Edit2, Folder, Plus, Tag, Trash2 } from "lucide-react";
import { useState } from "react";

interface CategoryFormData {
  name: string;
  isIncome: boolean;
  isGroup: boolean;
  groupId?: number;
  sortOrder?: number;
}

export function CategoryManagement() {
  const { data: categories = [], isLoading } = useCategories();
  const createCategory = useCreateCategory();
  const updateCategory = useUpdateCategory();
  const deleteCategory = useDeleteCategory();

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set());
  const [formData, setFormData] = useState<CategoryFormData>({
    name: "",
    isIncome: false,
    isGroup: false,
    groupId: undefined,
    sortOrder: undefined,
  });

  // Organize categories by groups
  const categoryGroups = categories
    .filter((cat) => !cat.isDeleted && cat.isGroup)
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

  const ungroupedCategories = categories
    .filter((cat) => !cat.isDeleted && !cat.isGroup && !cat.groupId)
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

  const getCategoriesInGroup = (groupId: number) =>
    categories
      .filter((cat) => !cat.isDeleted && !cat.isGroup && cat.groupId === groupId)
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

  const toggleGroup = (groupId: number) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupId)) {
      newExpanded.delete(groupId);
    } else {
      newExpanded.add(groupId);
    }
    setExpandedGroups(newExpanded);
  };

  const handleCreateClick = (isGroup: boolean, groupId?: number) => {
    setFormData({
      name: "",
      isIncome: false,
      isGroup,
      groupId,
      sortOrder: undefined,
    });
    setIsCreateDialogOpen(true);
  };

  const handleEditClick = (category: Category) => {
    setSelectedCategory(category);
    setFormData({
      name: category.name,
      isIncome: category.isIncome,
      isGroup: category.isGroup,
      groupId: category.groupId,
      sortOrder: category.sortOrder,
    });
    setIsEditDialogOpen(true);
  };

  const handleDeleteClick = (category: Category) => {
    setSelectedCategory(category);
    setIsDeleteDialogOpen(true);
  };

  const handleCreate = async () => {
    const data: CreateCategoryDTO = {
      name: formData.name,
      isIncome: formData.isIncome,
      isGroup: formData.isGroup,
      groupId: formData.groupId,
      sortOrder: formData.sortOrder,
    };

    await createCategory.mutateAsync(data);
    setIsCreateDialogOpen(false);
    setFormData({
      name: "",
      isIncome: false,
      isGroup: false,
      groupId: undefined,
      sortOrder: undefined,
    });
  };

  const handleUpdate = async () => {
    if (!selectedCategory) return;

    const data: UpdateCategoryDTO = {
      name: formData.name,
      isIncome: formData.isIncome,
      isGroup: formData.isGroup,
      groupId: formData.groupId,
      sortOrder: formData.sortOrder,
    };

    await updateCategory.mutateAsync({ id: selectedCategory.id, data });
    setIsEditDialogOpen(false);
    setSelectedCategory(null);
  };

  const handleDelete = async () => {
    if (!selectedCategory) return;

    await deleteCategory.mutateAsync(selectedCategory.id);
    setIsDeleteDialogOpen(false);
    setSelectedCategory(null);
  };

  const renderCategory = (category: Category, depth = 0) => (
    <div
      key={category.id}
      className="flex items-center justify-between py-2 px-3 hover:bg-muted/50 rounded-md group"
      style={{ paddingLeft: `${depth * 24 + 12}px` }}
    >
      <div className="flex items-center gap-2">
        <Tag className="size-4 text-muted-foreground" />
        <span className="font-medium">{category.name}</span>
        {category.isIncome && (
          <span className="text-xs px-2 py-0.5 bg-success/10 text-success rounded">Income</span>
        )}
      </div>
      <div className="flex items-center gap-1 transition-opacity pointer-fine:opacity-0 pointer-fine:group-hover:opacity-100 pointer-fine:group-focus-within:opacity-100">
        <Button
          variant="ghost"
          size="sm"
          aria-label={`Edit ${category.name}`}
          onClick={() => handleEditClick(category)}
        >
          <Edit2 className="size-3.5" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          aria-label={`Delete ${category.name}`}
          onClick={() => handleDeleteClick(category)}
        >
          <Trash2 className="size-3.5" />
        </Button>
      </div>
    </div>
  );

  const renderGroup = (group: Category) => {
    const isExpanded = expandedGroups.has(group.id);
    const groupCategories = getCategoriesInGroup(group.id);

    return (
      <div key={group.id} className="mb-2">
        <div className="flex items-center justify-between py-2 px-3 hover:bg-muted/50 rounded-md group">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => toggleGroup(group.id)}
              aria-label={`${isExpanded ? "Collapse" : "Expand"} ${group.name}`}
              aria-expanded={isExpanded}
              className="size-6 p-0"
            >
              {isExpanded ? (
                <ChevronDown className="size-4" />
              ) : (
                <ChevronRight className="size-4" />
              )}
            </Button>
            <Folder className="size-4 text-warning dark:text-warning" />
            <span className="font-semibold">{group.name}</span>
            <span className="text-xs text-muted-foreground">({groupCategories.length})</span>
            {group.isIncome && (
              <span className="text-xs px-2 py-0.5 bg-success/10 text-success rounded">Income</span>
            )}
          </div>
          <div className="flex items-center gap-1 transition-opacity pointer-fine:opacity-0 pointer-fine:group-hover:opacity-100 pointer-fine:group-focus-within:opacity-100">
            <Button variant="ghost" size="sm" onClick={() => handleCreateClick(false, group.id)}>
              <Plus className="size-3.5 mr-1" />
              Add Category
            </Button>
            <Button
              variant="ghost"
              size="sm"
              aria-label={`Edit ${group.name}`}
              onClick={() => handleEditClick(group)}
            >
              <Edit2 className="size-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              aria-label={`Delete ${group.name}`}
              onClick={() => handleDeleteClick(group)}
            >
              <Trash2 className="size-3.5" />
            </Button>
          </div>
        </div>
        {isExpanded && (
          <div className="ml-6 mt-1 space-y-1">
            {groupCategories.length === 0 ? (
              <div className="py-4 text-center text-sm text-muted-foreground">
                No categories in this group
              </div>
            ) : (
              groupCategories.map((cat) => renderCategory(cat, 1))
            )}
          </div>
        )}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-sm text-muted-foreground">Loading categories...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Category Groups</CardTitle>
              <CardDescription>Organize your transaction categories into groups</CardDescription>
            </div>
            <Button onClick={() => handleCreateClick(true)}>
              <Plus className="size-4 mr-2" />
              New Group
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {categoryGroups.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Folder className="size-12 text-muted-foreground/50 mb-4" />
              <p className="text-sm text-muted-foreground mb-2">No category groups yet</p>
              <Button variant="outline" size="sm" onClick={() => handleCreateClick(true)}>
                <Plus className="size-4 mr-2" />
                Create your first group
              </Button>
            </div>
          ) : (
            <div className="space-y-1">{categoryGroups.map((group) => renderGroup(group))}</div>
          )}
        </CardContent>
      </Card>

      {ungroupedCategories.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Ungrouped Categories</CardTitle>
                <CardDescription>Categories not assigned to any group</CardDescription>
              </div>
              <Button onClick={() => handleCreateClick(false)}>
                <Plus className="size-4 mr-2" />
                New Category
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-1">{ungroupedCategories.map((cat) => renderCategory(cat))}</div>
          </CardContent>
        </Card>
      )}

      {ungroupedCategories.length === 0 && categoryGroups.length > 0 && (
        <Button variant="outline" className="w-full" onClick={() => handleCreateClick(false)}>
          <Plus className="size-4 mr-2" />
          Add Ungrouped Category
        </Button>
      )}

      {/* Create Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create {formData.isGroup ? "Category Group" : "Category"}</DialogTitle>
            <DialogDescription>
              Add a new{" "}
              {formData.isGroup ? "group to organize categories" : "category for transactions"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={formData.isGroup ? "e.g., Food & Dining" : "e.g., Groceries"}
              />
            </div>
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Income Category</Label>
                <p className="text-sm text-muted-foreground">
                  Mark this as an income {formData.isGroup ? "group" : "category"}
                </p>
              </div>
              <Switch
                checked={formData.isIncome}
                onCheckedChange={(checked) => setFormData({ ...formData, isIncome: checked })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="sortOrder">Sort Order (optional)</Label>
              <Input
                id="sortOrder"
                type="number"
                value={formData.sortOrder ?? ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    sortOrder: e.target.value ? Number.parseInt(e.target.value) : undefined,
                  })
                }
                placeholder="0"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              disabled={!formData.name.trim() || createCategory.isPending}
            >
              {createCategory.isPending ? "Creating..." : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit {formData.isGroup ? "Category Group" : "Category"}</DialogTitle>
            <DialogDescription>
              Update the {formData.isGroup ? "group" : "category"} details
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Name</Label>
              <Input
                id="edit-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Income Category</Label>
                <p className="text-sm text-muted-foreground">
                  Mark this as an income {formData.isGroup ? "group" : "category"}
                </p>
              </div>
              <Switch
                checked={formData.isIncome}
                onCheckedChange={(checked) => setFormData({ ...formData, isIncome: checked })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-sortOrder">Sort Order (optional)</Label>
              <Input
                id="edit-sortOrder"
                type="number"
                value={formData.sortOrder ?? ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    sortOrder: e.target.value ? Number.parseInt(e.target.value) : undefined,
                  })
                }
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleUpdate}
              disabled={!formData.name.trim() || updateCategory.isPending}
            >
              {updateCategory.isPending ? "Updating..." : "Update"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Are you sure?</DialogTitle>
            <DialogDescription>
              This will delete "{selectedCategory?.name}".
              {selectedCategory?.isGroup &&
                getCategoriesInGroup(selectedCategory.id).length > 0 && (
                  <span className="block mt-2 text-destructive font-medium">
                    Warning: This group contains categories. They will become ungrouped.
                  </span>
                )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              disabled={deleteCategory.isPending}
              variant="destructive"
            >
              {deleteCategory.isPending ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
